/**
 * MusicPlayer 进度跟踪与 Seek（抽离自 MusicPlayer.js）
 * 返回 startProgressTracking, stopProgressTracking, handleSeekPointerDown/Move/UpOrCancel, hideSeekPreview, updateAllProgressVisual。
 * 通过 refsRef / callbacksRef 访问主文件 refs/callbacks，避免闭包陈旧。
 */
import { useCallback } from 'react';
import { formatTime } from './utils';
import { pctFromClientX, findNearestLyricIndex, applyMarqueeIfNeeded } from './useProgressSeek';
import { LYRIC_SYNC_THROTTLE_MS, SEEK_PREVIEW_RECT_CACHE_MS, SEEK_PREVIEW_HIDE_DELAY_MS } from './constants';

const GOLD_RGB = '251,191,36';
const GOLD_RGB2 = '245,158,11';

export function useProgressTracking({ refsRef, callbacksRef }) {
  const getAllProgressTracks = useCallback(() => {
    const refs = refsRef.current;
    const root = refs?.playerRef?.current;
    if (!root) return [];
    try {
      return Array.from(root.querySelectorAll('[data-progress-track]'));
    } catch (_) {
      return [];
    }
  }, [refsRef]);

  const updateAllProgressVisual = useCallback((pct) => {
    const clamped = Math.max(0, Math.min(1, pct));
    const tracks = getAllProgressTracks();
    if (!tracks || tracks.length === 0) return;
    for (const track of tracks) {
      const fill = track.querySelector('[data-progress-fill]');
      const knob = track.querySelector('[data-progress-knob]');
      if (fill) fill.style.width = `${clamped * 100}%`;
      if (knob) knob.style.left = `${clamped * 100}%`;
    }
  }, [getAllProgressTracks]);

  const hideSeekPreview = useCallback(() => {
    const refs = refsRef.current;
    const allBoxes = document.querySelectorAll('[data-seek-preview]');
    if (allBoxes.length > 0) {
      allBoxes.forEach(box => {
        box.style.opacity = '0';
        box.style.transform = 'translateX(-50%) translateY(-100%) translateY(-2px) scale(0.98)';
      });
    } else {
      const box = refs?.seekPreviewRef?.current;
      if (box) {
        box.style.opacity = '0';
        box.style.transform = 'translateX(-50%) translateY(-100%) translateY(-2px) scale(0.98)';
      }
    }
    if (refs?.seekPreviewVisibleRef) refs.seekPreviewVisibleRef.current = false;
    if (refs?.seekPreviewTextAnimRef?.current) {
      try { refs.seekPreviewTextAnimRef.current.cancel(); } catch (_) {}
      refs.seekPreviewTextAnimRef.current = null;
    }
    if (refs?.seekPreviewTransAnimRef?.current) {
      try { refs.seekPreviewTransAnimRef.current.cancel(); } catch (_) {}
      refs.seekPreviewTransAnimRef.current = null;
    }
    if (refs?.seekPreviewHideTimerRef?.current) {
      clearTimeout(refs.seekPreviewHideTimerRef.current);
    }
    if (refs?.seekPreviewHideTimerRef) {
      refs.seekPreviewHideTimerRef.current = setTimeout(() => {
        if (!refs?.seekPreviewVisibleRef?.current && refs?.seekPreviewRafRef?.current) {
          cancelAnimationFrame(refs.seekPreviewRafRef.current);
          refs.seekPreviewRafRef.current = null;
        }
      }, SEEK_PREVIEW_HIDE_DELAY_MS);
    }
  }, [refsRef]);

  const updateSeekPreview = useCallback((clientX, pct, dur, trackEl) => {
    const refs = refsRef.current;
    const callbacks = callbacksRef.current;
    const track = trackEl || refs?.progressTrackRef?.current;
    if (!track || !dur || dur <= 0) return;
    const box = refs?.seekPreviewRef?.current;
    if (!box) return;
    const textEl = box.querySelector('[data-seek-preview-text]') || refs?.seekPreviewTextRef?.current;
    if (!textEl) return;
    const lyricsArr = refs?.lyricsRef?.current;
    if (!lyricsArr || lyricsArr.length === 0) return;

    const tRaw = Math.max(0, Math.min(dur, pct * dur));
    const tForLyric = tRaw + (refs?.lyricOffsetRef?.current || 0);
    const idx = findNearestLyricIndex(tForLyric, lyricsArr);
    const line = lyricsArr[idx];
    if (!line || !line.text) return;

    const now = Date.now();
    let rect = refs?.progressTrackRectRef?.current;
    if (!rect || now - (refs?.progressTrackRectTimestampRef?.current || 0) > SEEK_PREVIEW_RECT_CACHE_MS) {
      rect = track.getBoundingClientRect();
      if (refs?.progressTrackRectRef) refs.progressTrackRectRef.current = rect;
      if (refs?.progressTrackRectTimestampRef) refs.progressTrackRectTimestampRef.current = now;
    }
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const leftInTrack = Math.max(12, Math.min(rect.width - 12, x));
    const left = rect.left + leftInTrack;
    const top = Math.max(8, rect.top - 8);
    box.style.top = `${top}px`;
    if (refs?.seekPreviewTargetXRef) refs.seekPreviewTargetXRef.current = left;
    if (!refs?.seekPreviewVisibleRef?.current) {
      if (refs?.seekPreviewXRef) refs.seekPreviewXRef.current = left;
      box.style.left = `${left}px`;
    }
    if (refs?.seekPreviewVisibleRef) refs.seekPreviewVisibleRef.current = true;
    box.style.opacity = '1';
    box.style.transform = 'translateX(-50%) translateY(-100%) translateY(-8px) scale(1)';
    if (refs?.seekPreviewHideTimerRef?.current) {
      clearTimeout(refs.seekPreviewHideTimerRef.current);
      refs.seekPreviewHideTimerRef.current = null;
    }
    box.style.borderColor = `rgba(${GOLD_RGB}, 0.44)`;
    box.style.boxShadow = `0 18px 42px rgba(0,0,0,0.55), 0 0 26px rgba(${GOLD_RGB2}, 0.22)`;

    const timeEl = box.querySelector('[data-seek-preview-time]') || refs?.seekPreviewTimeRef?.current;
    if (timeEl) {
      const mins = Math.floor(tRaw / 60);
      const secs = Math.floor(tRaw % 60);
      timeEl.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
      timeEl.style.backgroundColor = `rgba(${GOLD_RGB2}, 0.22)`;
      timeEl.style.borderColor = `rgba(${GOLD_RGB}, 0.42)`;
    }
    textEl.textContent = line.text;
    applyMarqueeIfNeeded(textEl, refs?.seekPreviewTextAnimRef, { speedPxPerSec: 44, pauseMs: 520 });

    const transEl = box.querySelector('[data-seek-preview-trans]') || refs?.seekPreviewTransRef?.current;
    if (transEl) {
      const lyricSettings = callbacks?.lyricSettings;
      if (lyricSettings?.showTranslation && line.translation) {
        transEl.textContent = line.translation;
        transEl.style.display = '';
        applyMarqueeIfNeeded(transEl, refs?.seekPreviewTransAnimRef, { speedPxPerSec: 46, pauseMs: 520 });
      } else {
        transEl.textContent = '';
        transEl.style.display = 'none';
        if (refs?.seekPreviewTransAnimRef?.current) {
          try { refs.seekPreviewTransAnimRef.current.cancel(); } catch (_) {}
          refs.seekPreviewTransAnimRef.current = null;
        }
      }
    }
    if (refs?.currentSeekPreviewBoxRef) refs.currentSeekPreviewBoxRef.current = box;
    if (refs?.seekPreviewLyricIdxRef?.current !== idx) {
      if (refs?.seekPreviewLyricIdxRef) refs.seekPreviewLyricIdxRef.current = idx;
      callbacks?.updateMiniLyric?.(idx);
    }
    if (!refs?.seekPreviewRafRef?.current) {
      const tick = () => {
        let el = refs?.currentSeekPreviewBoxRef?.current;
        if (!el) {
          const allBoxes = document.querySelectorAll('[data-seek-preview]');
          el = allBoxes.length > 0 ? allBoxes[0] : refs?.seekPreviewRef?.current;
        }
        if (!el) {
          if (refs?.seekPreviewRafRef) refs.seekPreviewRafRef.current = null;
          return;
        }
        const nowMs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        const prevTs = refs?.seekPreviewLastTsRef?.current || nowMs;
        const dt = Math.max(8, Math.min(34, nowMs - prevTs));
        if (refs?.seekPreviewLastTsRef) refs.seekPreviewLastTsRef.current = nowMs;
        const tgt = refs?.seekPreviewTargetXRef?.current || 0;
        const prevTgt = refs?.seekPreviewLastTargetXRef?.current ?? tgt;
        if (refs?.seekPreviewLastTargetXRef) refs.seekPreviewLastTargetXRef.current = tgt;
        const v = (tgt - prevTgt) / dt;
        const vel = (refs?.seekPreviewVelRef?.current || 0) * 0.75 + v * 0.25;
        if (refs?.seekPreviewVelRef) refs.seekPreviewVelRef.current = vel;
        const speed = Math.abs(vel);
        const lerp = Math.max(0.18, Math.min(0.42, 0.18 + (speed / 2.4) * 0.24));
        const cur = refs?.seekPreviewXRef?.current ?? tgt;
        const next = cur + (tgt - cur) * lerp;
        if (refs?.seekPreviewXRef) refs.seekPreviewXRef.current = next;
        el.style.left = `${next}px`;
        if (refs?.seekPreviewVisibleRef?.current) {
          const scale = 1 + Math.max(0, Math.min(0.02, speed * 0.012));
          el.style.transform = `translateX(-50%) translateY(-100%) translateY(-8px) scale(${scale.toFixed(3)})`;
        }
        if (refs?.seekPreviewVisibleRef?.current || refs?.isSeekingRef?.current) {
          if (refs?.seekPreviewRafRef) refs.seekPreviewRafRef.current = requestAnimationFrame(tick);
        } else if (refs?.seekPreviewRafRef) {
          refs.seekPreviewRafRef.current = null;
        }
      };
      if (refs?.seekPreviewRafRef) refs.seekPreviewRafRef.current = requestAnimationFrame(tick);
    }
  }, [refsRef, callbacksRef]);

  const commitSeek = useCallback((pct) => {
    const refs = refsRef.current;
    const callbacks = callbacksRef.current;
    const hasVideo = callbacks?.hasVideo;
    const mediaElement = (hasVideo && refs?.videoRef?.current) ? refs.videoRef.current : refs?.audioRef?.current;
    const dur = mediaElement?.duration || refs?.durationRef?.current || 0;
    if (!mediaElement || !dur || dur <= 0) return;
    const clamped = Math.max(0, Math.min(1, pct));
    const targetTime = Math.max(0, Math.min(dur, clamped * dur));
    // iOS/Safari + webm 偶发：seek 后进入“卡死不前进”，且 play/pause 无法恢复。
    // 处理策略：
    // - 拖拽时若原本在播放：pointerDown 先 pause；pointerUp 设置 currentTime 后再恢复 play（更稳）。
    // - 若恢复后在一段时间内仍无进度：对 audio 做一次轻量“重载 src”并重建音频图，然后再 play。
    mediaElement.currentTime = targetTime;
    if (refs?.currentLyricIndexRef) refs.currentLyricIndexRef.current = -1;

    const shouldResume = !!refs?.seekWasPlayingRef?.current;
    if (refs?.seekWasPlayingRef) refs.seekWasPlayingRef.current = false;
    if (!shouldResume) return;

    const tryResume = async () => {
      try { await callbacks?.resumeAudioContext?.(); } catch (_) {}
      try { await mediaElement.play(); } catch (_) {}
    };

    // 先等 seeked 再尝试 play；同时放一个短延时兜底（某些情况下 seeked 不触发）
    try {
      let done = false;
      const onSeeked = () => {
        done = true;
        tryResume();
      };
      mediaElement.addEventListener('seeked', onSeeked, { once: true });
      setTimeout(() => { if (!done) tryResume(); }, 240);
    } catch (_) {
      setTimeout(() => { tryResume(); }, 0);
    }

    // Watchdog：仅对 webm 音频启用，避免影响 mp3/flac 等正常情况
    const isWebm = String(refs?.currentPlayingFormatRef?.current?.format || '').toLowerCase() === 'webm';
    const isAudioEl = String(mediaElement?.tagName || '').toUpperCase() === 'AUDIO';
    if (!isWebm || !isAudioEl) return;

    const token = (refs?.seekRecoverTokenRef?.current || 0) + 1;
    if (refs?.seekRecoverTokenRef) refs.seekRecoverTokenRef.current = token;

    setTimeout(async () => {
      const refs2 = refsRef.current;
      const callbacks2 = callbacksRef.current;
      const el = refs2?.audioRef?.current;
      if (!el || el !== mediaElement) return;
      if (!refs2?.seekRecoverTokenRef || refs2.seekRecoverTokenRef.current !== token) return;
      if (!callbacks2?.playing) return;

      // 判断“卡死”：目标时间附近停住 + readyState 不足以播放
      const readyOk = typeof el.readyState === 'number' ? el.readyState >= 3 : false; // HAVE_FUTURE_DATA
      const stuckNearTarget = Math.abs((el.currentTime || 0) - targetTime) < 0.25;
      if (readyOk || !stuckNearTarget) return;

      const src = el.currentSrc || el.src || '';
      if (!src) return;
      const safeTime = Math.max(0, Math.min((el.duration || dur || 0) - 0.05, targetTime));

      try { el.pause(); } catch (_) {}
      try { el.removeAttribute('src'); } catch (_) {}
      try { el.load(); } catch (_) {}
      try { el.src = src; } catch (_) {}
      try { el.load(); } catch (_) {}

      // 等待 metadata/canplay，避免立刻设 currentTime 失败
      await new Promise((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          try { el.removeEventListener('loadedmetadata', done); } catch (_) {}
          try { el.removeEventListener('canplay', done); } catch (_) {}
          resolve();
        };
        try { el.addEventListener('loadedmetadata', done, { once: true }); } catch (_) {}
        try { el.addEventListener('canplay', done, { once: true }); } catch (_) {}
        setTimeout(done, 1200);
      });

      try { el.currentTime = safeTime; } catch (_) {}
      try { await callbacks2?.resumeAudioContext?.(); } catch (_) {}
      try { await callbacks2?.rebuildAudioGraph?.('audio'); } catch (_) {}
      try { await el.play(); } catch (_) {}
    }, 1800);
  }, [refsRef, callbacksRef]);

  const startProgressTracking = useCallback(() => {
    const refs = refsRef.current;
    const callbacks = callbacksRef.current;
    if (refs?.isProgressTrackingRef?.current) return;
    if (refs?.isProgressTrackingRef) refs.isProgressTrackingRef.current = true;

    const startProgressRaf = () => {
      if (refs?.progressRafRef?.current) return;
      const tick = () => {
        const audio = refs?.audioRef?.current;
        const video = refs?.videoRef?.current;
        const timeElDesktop = refs?.currentTimeTextRefDesktop?.current;
        const timeElMobile = refs?.currentTimeTextRefMobile?.current;
        if (refs?.isSeekingRef?.current) {
          if (refs?.progressRafRef) refs.progressRafRef.current = requestAnimationFrame(tick);
          return;
        }
        const hasVideo = callbacks?.hasVideo;
        const mediaElement = (hasVideo && video) ? video : audio;
        const dur = mediaElement?.duration || refs?.durationRef?.current || 0;
        const ab = refs?.abLoopRef?.current;
        if (ab?.enabled && mediaElement && dur > 0 && Number.isFinite(ab.start) && Number.isFinite(ab.end) && ab.end > ab.start) {
          if (mediaElement.currentTime >= ab.end) {
            try { mediaElement.currentTime = Math.min(ab.start, dur - 0.01); } catch (_) {}
          }
        }
        if (mediaElement && dur > 0) {
          const pct = Math.max(0, Math.min(1, mediaElement.currentTime / dur));
          updateAllProgressVisual(pct);
        }
        const fadeInOut = callbacks?.fadeInOut;
        const hasVideoInCurrentMusic = callbacks?.hasVideoInCurrentMusic;
        const shouldShowCoverWindow = callbacks?.shouldShowCoverWindow;
        const visualFadeMs = callbacks?.visualFadeMs;
        if (fadeInOut && (hasVideoInCurrentMusic || shouldShowCoverWindow) && mediaElement && dur > 0) {
          const remaining = Math.max(0, dur - (mediaElement.currentTime || 0));
          const fadeSeconds = (visualFadeMs || 0) / 1000;
          if (fadeSeconds > 0 && remaining <= fadeSeconds && !refs?.coverFadeOutStartedRef?.current) {
            if (refs?.coverFadeOutStartedRef) refs.coverFadeOutStartedRef.current = true;
            if (refs?.coverActiveLayerRef?.current === 'A') callbacks?.setCoverLayerAOpacity?.(0);
            else callbacks?.setCoverLayerBOpacity?.(0);
          }
        }
        if (mediaElement) {
          const sec = Math.floor(mediaElement.currentTime || 0);
          if (sec !== (refs?.lastTimeLabelSecRef?.current ?? -1)) {
            const t = formatTime(sec);
            if (timeElDesktop) timeElDesktop.textContent = t;
            if (timeElMobile) timeElMobile.textContent = t;
            if (refs?.lastTimeLabelSecRef) refs.lastTimeLabelSecRef.current = sec;
          }
        }
        if (mediaElement) {
          const nowMs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          if (nowMs - (refs?.lastLyricSyncMsRef?.current || 0) >= LYRIC_SYNC_THROTTLE_MS) {
            const currentLyrics = refs?.lyricsRef?.current || [];
            if (currentLyrics.length > 0) {
              const t = (mediaElement.currentTime || 0) + (refs?.lyricOffsetRef?.current || 0);
              let idx = refs?.currentLyricIndexRef?.current ?? -1;
              if (idx < -1) idx = -1;
              if (idx >= currentLyrics.length) idx = currentLyrics.length - 1;
              while (idx + 1 < currentLyrics.length && t >= currentLyrics[idx + 1].time) idx++;
              while (idx >= 0 && t < currentLyrics[idx].time) idx--;
              if (refs?.currentLyricIndexRef) refs.currentLyricIndexRef.current = idx;
              if (idx !== refs?.currentLyricLineRef?.current) {
                callbacks?.applyLyricHighlight?.(idx);
              }
            }
            if (refs?.lastLyricSyncMsRef) refs.lastLyricSyncMsRef.current = nowMs;
          }
        }
        if (refs?.progressRafRef) refs.progressRafRef.current = requestAnimationFrame(tick);
      };
      if (refs?.progressRafRef) refs.progressRafRef.current = requestAnimationFrame(tick);
    };
    startProgressRaf();
  }, [refsRef, callbacksRef, updateAllProgressVisual]);

  const stopProgressTracking = useCallback(() => {
    const refs = refsRef.current;
    if (refs?.isProgressTrackingRef) refs.isProgressTrackingRef.current = false;
    if (refs?.progressIntervalRef?.current) {
      clearInterval(refs.progressIntervalRef.current);
      refs.progressIntervalRef.current = null;
    }
    if (refs?.progressRafRef?.current) {
      cancelAnimationFrame(refs.progressRafRef.current);
      refs.progressRafRef.current = null;
    }
    hideSeekPreview();
  }, [refsRef, hideSeekPreview]);

  const updateProgressVisual = useCallback((pct) => {
    updateAllProgressVisual(pct);
  }, [updateAllProgressVisual]);

  const handleSeekPointerDown = useCallback((e) => {
    const refs = refsRef.current;
    const callbacks = callbacksRef.current;
    const track = e.currentTarget;
    const hasVideo = callbacks?.hasVideo;
    const mediaElement = (hasVideo && refs?.videoRef?.current) ? refs.videoRef.current : refs?.audioRef?.current;
    const dur = mediaElement?.duration || refs?.durationRef?.current || 0;
    if (!track || !dur || dur <= 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (refs?.isSeekingRef) refs.isSeekingRef.current = true;
    if (refs?.seekPointerIdRef) refs.seekPointerIdRef.current = e.pointerId;
    try { track.setPointerCapture(e.pointerId); } catch (_) {}
    // 拖拽时先暂停（若原本在播放），避免 seek 过程中解码/缓冲抖动导致卡死
    try {
      const wasActuallyPlaying = !!(mediaElement && !mediaElement.paused && !mediaElement.ended);
      if (refs?.seekWasPlayingRef) refs.seekWasPlayingRef.current = wasActuallyPlaying;
      if (wasActuallyPlaying && mediaElement) mediaElement.pause();
    } catch (_) {}
    const pct = pctFromClientX(e.clientX, track);
    updateProgressVisual(pct);
    updateSeekPreview(e.clientX, pct, dur, track);
    const knob = refs?.progressKnobRef?.current;
    if (knob) knob.style.opacity = '1';
  }, [refsRef, callbacksRef, updateProgressVisual, updateSeekPreview]);

  const handleSeekPointerMove = useCallback((e) => {
    const refs = refsRef.current;
    const callbacks = callbacksRef.current;
    const track = e.currentTarget;
    if (!track || !refs?.isSeekingRef?.current) return;
    if (refs?.seekPointerIdRef?.current != null && e.pointerId !== refs.seekPointerIdRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const pct = pctFromClientX(e.clientX, track);
    updateProgressVisual(pct);
    const hasVideo = callbacks?.hasVideo;
    const mediaElement = (hasVideo && refs?.videoRef?.current) ? refs.videoRef.current : refs?.audioRef?.current;
    const dur = mediaElement?.duration || refs?.durationRef?.current || 0;
    updateSeekPreview(e.clientX, pct, dur, track);
  }, [refsRef, callbacksRef, updateProgressVisual, updateSeekPreview]);

  const handleSeekPointerUpOrCancel = useCallback((e) => {
    const refs = refsRef.current;
    const track = e.currentTarget;
    if (!track || !refs?.isSeekingRef?.current) return;
    if (refs?.seekPointerIdRef?.current != null && e.pointerId !== refs.seekPointerIdRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const pct = pctFromClientX(e.clientX, track);
    updateProgressVisual(pct);
    commitSeek(pct);
    hideSeekPreview();
    if (refs?.isSeekingRef) refs.isSeekingRef.current = false;
    if (refs?.seekPointerIdRef) refs.seekPointerIdRef.current = null;
    if (refs?.seekPreviewLyricIdxRef) refs.seekPreviewLyricIdxRef.current = -1;
    try { track.releasePointerCapture(e.pointerId); } catch (_) {}
  }, [refsRef, updateProgressVisual, commitSeek, hideSeekPreview]);

  return {
    startProgressTracking,
    stopProgressTracking,
    handleSeekPointerDown,
    handleSeekPointerMove,
    handleSeekPointerUpOrCancel,
    hideSeekPreview,
    updateAllProgressVisual
  };
}
